import React, { useState } from 'react';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import ProgressBar from './ProgressBar';
import { questions } from '../data/questions';
import { getTierColor, getTierAdvice } from '../utils/scoring';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackDownloadReport, trackBookCall } from '../utils/metaPixel';

const { FiTrendingUp, FiExternalLink, FiCalendar, FiDownload, FiAlertCircle, FiChevronDown, FiChevronUp, FiAlertTriangle, FiTool, FiCheckCircle, FiTarget, FiGlobe, FiBarChart2 } = FiIcons;

const ResultsSection = ({ name, overallScore, categoryScores, tier, formData, responses, detailedResponses, submissionError }) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Find the 3 lowest scoring categories based on percentage of max possible score
  const getTopFixesNeeded = () => {
    const categoryPercentages = Object.entries(categoryScores).map(([category, score]) => {
      const maxScore = questions[category].length * 2;
      const percentage = (score / maxScore) * 100;
      return {
        category,
        percentage,
        score,
        maxScore
      };
    });

    // Sort by percentage (ascending) and return all 3 categories
    return categoryPercentages.sort((a, b) => a.percentage - b.percentage);
  };

  // Specific fix recommendations based on category
  const getFixRecommendation = (category) => {
    switch(category) {
      case "Brand Positioning":
        return "Clarify your unique positioning and create professional brand materials that attract your ideal customers.";
      case "Website Performance":
        return "Optimise your website for lead generation with clear messaging, prominent testimonials, and friction-free booking.";
      case "Marketing System":
        return "Implement automated marketing systems with consistent content strategy and lead tracking.";
      default:
        return "Review and improve your processes in this area.";
    }
  };

  // Service recommendations based on tier
  const getServiceRecommendations = (tier) => {
    const recommendations = {
      "Needs Professional Help": {
        services: [
          { name: "Complete Rebrand", outcome: "Professional positioning" },
          { name: "Website Redesign", outcome: "+200% more enquiries" },
          { name: "Marketing System Setup", outcome: "Automated lead nurturing" }
        ],
        roi: "5-10x ROI in first year",
        primaryCTA: "Get Your Complete Business Audit",
        description: "Complete rebrand + website redesign + marketing system setup"
      },
      "Ready to Scale": {
        services: [
          { name: "Website Optimisation", outcome: "+40% more leads" },
          { name: "Marketing Automation", outcome: "+60% conversion rate" },
          { name: "Conversion Improvements", outcome: "+£2k monthly revenue" }
        ],
        roi: "3-5x return in first year",
        primaryCTA: "Get Your Scaling Blueprint",
        description: "Website optimisation + marketing automation + conversion improvements"
      },
      "Optimisation Opportunities": {
        services: [
          { name: "Advanced Marketing Optimisation", outcome: "+25% lead quality" },
          { name: "Conversion Rate Improvements", outcome: "+£1k monthly revenue" },
          { name: "Scaling Strategies", outcome: "Predictable growth system" }
        ],
        roi: "2-4x return in first year",
        primaryCTA: "Get Your Advanced Strategy Session",
        description: "Advanced marketing optimisation + conversion improvements + scaling strategies"
      },
      "Industry Leader": {
        services: [
          { name: "Advanced Growth Strategies", outcome: "Market expansion" },
          { name: "Market Expansion Support", outcome: "Multi-location growth" },
          { name: "Competitive Advantage Protection", outcome: "Industry leadership" }
        ],
        roi: "Bespoke ROI projections",
        primaryCTA: "Get Your Market Domination Strategy",
        description: "Advanced growth strategies + market expansion + competitive advantage protection"
      }
    };

    return recommendations[tier];
  };

  const topFixesNeeded = getTopFixesNeeded();
  const serviceRecommendations = getServiceRecommendations(tier);

  const generatePDFReport = () => {
    // Track download event
    trackDownloadReport();

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace = 10) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // Helper to add wrapped text
    const addWrappedText = (text, fontSize, isBold = false, color = [0, 0, 0]) => {
      doc.setFontSize(fontSize);
      doc.setFont(undefined, isBold ? 'bold' : 'normal');
      doc.setTextColor(color[0], color[1], color[2]);
      const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
      lines.forEach(line => {
        checkPageBreak();
        doc.text(line, margin, yPos);
        yPos += fontSize * 0.5;
      });
      yPos += 3;
    };

    // Header - Brand
    doc.setFillColor(217, 4, 41); // Primary red color
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text('FITNESS BRAND HEALTH CHECK', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('PulseFit Marketing', pageWidth / 2, 23, { align: 'center' });

    yPos = 45;

    // Date and participant info
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 15;

    // Overall Score - Large and prominent
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');
    doc.setFontSize(48);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(217, 4, 41);
    doc.text(`${overallScore}%`, pageWidth / 2, yPos + 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text('Overall Brand Health Score', pageWidth / 2, yPos + 30, { align: 'center' });
    yPos += 45;

    // Tier Badge
    const tierColors = {
      'Industry Leader': [34, 197, 94],
      'Optimisation Opportunities': [59, 130, 246],
      'Ready to Scale': [249, 115, 22],
      'Needs Professional Help': [239, 68, 68]
    };
    const tierColor = tierColors[tier] || [100, 100, 100];
    doc.setFillColor(tierColor[0], tierColor[1], tierColor[2]);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 12, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(`Tier: ${tier}`, pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 25;

    // Participant Information
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Your Information', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Name: ${formData?.name || name}`, margin, yPos);
    yPos += 6;
    doc.text(`Email: ${formData?.email || ''}`, margin, yPos);
    if (formData?.company) {
      yPos += 6;
      doc.text(`Company: ${formData.company}`, margin, yPos);
    }
    if (formData?.role) {
      yPos += 6;
      doc.text(`Role: ${formData.role}`, margin, yPos);
    }
    yPos += 15;

    // Top 3 Fixes
    checkPageBreak(40);
    doc.setFillColor(217, 4, 41);
    doc.rect(margin, yPos, 4, 8, 'F');
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Your Top 3 Priority Fixes', margin + 8, yPos + 6);
    yPos += 15;

    topFixesNeeded.forEach((item, index) => {
      checkPageBreak(25);

      // Fix number circle
      doc.setFillColor(217, 4, 41);
      doc.circle(margin + 5, yPos + 3, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`${index + 1}`, margin + 5, yPos + 5, { align: 'center' });

      // Category name and score
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text(`${item.category}`, margin + 12, yPos + 5);
      doc.setFontSize(9);
      doc.setTextColor(239, 68, 68);
      doc.text(`${Math.round(item.percentage)}% (${item.score}/${item.maxScore})`, pageWidth - margin - 30, yPos + 5);

      yPos += 8;

      // Recommendation
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.setFont(undefined, 'normal');
      const recLines = doc.splitTextToSize(getFixRecommendation(item.category), pageWidth - 2 * margin - 10);
      recLines.forEach(line => {
        doc.text(line, margin + 12, yPos);
        yPos += 4;
      });

      yPos += 8;
    });

    // Category Breakdown
    checkPageBreak(40);
    doc.setFillColor(217, 4, 41);
    doc.rect(margin, yPos, 4, 8, 'F');
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Category Breakdown', margin + 8, yPos + 6);
    yPos += 15;

    Object.entries(categoryScores).forEach(([category, score]) => {
      checkPageBreak(30);
      const maxScore = questions[category].length * 2;
      const percentage = Math.round((score / maxScore) * 100);

      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(category, margin, yPos);
      yPos += 6;

      // Progress bar
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 6, 1, 1, 'FD');
      doc.setFillColor(217, 4, 41);
      const barWidth = ((pageWidth - 2 * margin) * percentage) / 100;
      doc.roundedRect(margin, yPos, barWidth, 6, 1, 1, 'F');

      // Percentage text
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(`${percentage}%`, pageWidth - margin, yPos + 4, { align: 'right' });

      yPos += 15;
    });

    // Recommendations
    checkPageBreak(40);
    doc.setFillColor(217, 4, 41);
    doc.rect(margin, yPos, 4, 8, 'F');
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('What This Means', margin + 8, yPos + 6);
    yPos += 12;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    const adviceLines = doc.splitTextToSize(getTierAdvice(tier), pageWidth - 2 * margin);
    adviceLines.forEach(line => {
      checkPageBreak();
      doc.text(line, margin, yPos);
      yPos += 5;
    });
    yPos += 10;

    // Next Steps / CTA
    checkPageBreak(30);
    doc.setFillColor(217, 4, 41);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Ready to Improve Your Brand Health?', pageWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Book your free strategy call:', pageWidth / 2, yPos + 17, { align: 'center' });
    doc.text('www.pulsefitmarketing.com/contact', pageWidth / 2, yPos + 22, { align: 'center' });

    // Footer on all pages
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text('© PulseFit Marketing - www.pulsefitmarketing.com', pageWidth / 2, pageHeight - 5, { align: 'center' });
    }

    // Download the PDF
    const fileName = `Brand_Health_Report_${(formData?.name || name).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const handleBookCall = () => {
    // Track booking event
    trackBookCall();
    // Continue with normal link behaviour
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch(category) {
      case "Brand Positioning":
        return FiTarget;
      case "Website Performance":
        return FiGlobe;
      case "Marketing System":
        return FiBarChart2;
      default:
        return FiTool;
    }
  };

  // Get priority color
  const getPriorityColor = (index) => {
    switch(index) {
      case 0:
        return 'text-danger-500 border-danger-500 bg-danger-500';
      case 1:
        return 'text-orange-500 border-orange-500 bg-orange-500';
      case 2:
        return 'text-yellow-500 border-yellow-500 bg-yellow-500';
      default:
        return 'text-primary-500 border-primary-500 bg-primary-500';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="space-y-8"
    >
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="inline-flex items-center space-x-3 mb-4"
        >
          <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-primary-600" />
          <h2 className="text-2xl md:text-3xl font-bold text-white">Thank you, {name}!</h2>
        </motion.div>
        <p className="text-base md:text-lg text-gray-300">Here are your brand health results:</p>
      </div>

      {submissionError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-500 border-l-4 border-danger-600 p-4 rounded-md"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiAlertCircle} className="text-danger-500 w-5 h-5 mr-3" />
            <p className="text-gray-300">
              {submissionError} Your results are still available below.
            </p>
          </div>
        </motion.div>
      )}

      {/* Enhanced Score Display - Much Larger */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="gradient-dark-blue rounded-2xl border-2 border-primary-600 p-8 md:p-12 text-center soft-shadow grain-texture relative overflow-hidden"
      >
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary-600 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative z-10">
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{
              duration: 0.8,
              delay: 0.6,
              type: "spring",
              stiffness: 100
            }}
            className="mb-6"
          >
            <div className="text-7xl md:text-8xl lg:text-9xl font-black text-primary-600 mb-4 tracking-tight" style={{ textShadow: '0 0 40px rgba(217, 4, 41, 0.3)' }}>
              {overallScore}%
            </div>
            <div className="text-xl md:text-2xl text-gray-200 font-medium">Overall Brand Health Score</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <div className={`inline-flex items-center px-6 py-3 md:px-8 md:py-4 rounded-full text-lg md:text-xl font-bold ${getTierColor(tier)} shadow-xl`}>
              Tier: {tier}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Download PDF Button - Prominent */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="text-center"
      >
        <button
          onClick={generatePDFReport}
          className="inline-flex items-center justify-center px-8 py-4 bg-dark-500 border-2 border-primary-600 text-white font-semibold text-lg rounded-lg hover:bg-dark-400 transition-all duration-300 shadow-lg hover:shadow-xl group"
        >
          <SafeIcon icon={FiDownload} className="w-5 h-5 mr-3 group-hover:animate-bounce" />
          Download Full Report (PDF)
        </button>
      </motion.div>

      {/* Top 3 Fixes Section - Enhanced */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-dark-500 rounded-xl border-2 border-primary-600 p-6 md:p-8 soft-shadow radial-gradient-dark texture-overlay"
      >
        <div className="flex items-center mb-6">
          <SafeIcon icon={FiTool} className="w-7 h-7 text-primary-600 mr-3" />
          <h3 className="text-2xl md:text-3xl font-bold text-white">Your Top 3 Priority Fixes</h3>
        </div>
        <div className="space-y-5">
          {topFixesNeeded.map((item, index) => {
            const IconComponent = getCategoryIcon(item.category);
            const priorityColors = getPriorityColor(index);

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + (index * 0.1) }}
                className="bg-dark-600 rounded-xl p-5 md:p-6 border-2 border-dark-400 hover:border-primary-600 transition-all duration-300 inner-shadow glass-effect group"
              >
                <div className="flex items-start gap-4">
                  {/* Large Priority Number Badge */}
                  <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full border-4 ${priorityColors.replace('bg-', 'border-')} bg-opacity-20 flex items-center justify-center`}>
                    <span className={`text-2xl md:text-3xl font-black ${priorityColors.split(' ')[0]}`}>
                      {index + 1}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <h4 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                        <SafeIcon icon={IconComponent} className="w-5 h-5 text-primary-600" />
                        {item.category}
                      </h4>
                      <span className={`text-sm md:text-base font-bold px-3 py-1 rounded-full ${priorityColors.replace('bg-', 'border-')} border-2 ${priorityColors.split(' ')[0]} bg-opacity-10`}>
                        {Math.round(item.percentage)}% ({item.score}/{item.maxScore})
                      </span>
                    </div>
                    <p className="text-gray-300 leading-relaxed mb-3">{getFixRecommendation(item.category)}</p>

                    {/* Impact indicator */}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-400">Priority:</span>
                      <span className={`font-semibold ${priorityColors.split(' ')[0]}`}>
                        {index === 0 ? 'CRITICAL' : index === 1 ? 'HIGH' : 'MEDIUM'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Main CTA - Single, Prominent */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="text-center"
      >
        <div className="bg-gradient-to-br from-dark-500 to-dark-600 rounded-xl border-2 border-primary-600 p-8 md:p-10 soft-shadow">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Fix These Issues?</h3>
          <p className="text-gray-300 mb-6 text-lg max-w-2xl mx-auto">
            {getTierAdvice(tier)}
          </p>
          <a
            href="https://www.pulsefitmarketing.com/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-10 py-5 gradient-primary text-white font-bold text-xl rounded-xl hover:bg-primary-700 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105"
            onClick={handleBookCall}
            style={{
              background: 'linear-gradient(135deg, #d90429 0%, #ef233c 100%)',
            }}
          >
            {serviceRecommendations.primaryCTA}
            <SafeIcon icon={FiExternalLink} className="w-6 h-6 ml-3" />
          </a>
          <p className="text-gray-400 text-sm mt-4">Free strategy call • No obligation • Tailored to your tier</p>
        </div>
      </motion.div>

      <div className="grid gap-5 md:gap-6">
        <h3 className="text-xl font-semibold text-white">Category Breakdown</h3>

        {Object.entries(categoryScores).map(([category, score], index) => {
          const maxScore = questions[category].length * 2;
          const percentage = Math.round((score / maxScore) * 100);
          const isExpanded = expandedCategories[category];

          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.8 + (index * 0.1) }}
              className="bg-dark-500 rounded-lg border border-dark-400 overflow-hidden soft-shadow grain-texture"
            >
              <div className="p-4">
                <ProgressBar
                  value={score}
                  max={maxScore}
                  label={category}
                />
                <button
                  onClick={() => toggleCategory(category)}
                  className="mt-3 flex items-center text-sm text-gray-400 hover:text-primary-300 transition-colors"
                >
                  {isExpanded ? (
                    <>Hide Detailed Answers <SafeIcon icon={FiChevronUp} className="ml-1" /></>
                  ) : (
                    <>View Detailed Answers <SafeIcon icon={FiChevronDown} className="ml-1" /></>
                  )}
                </button>
              </div>

              {isExpanded && detailedResponses && detailedResponses[category] && (
                <div className="px-4 pb-4 border-t border-dark-400 pt-3">
                  <div className="space-y-3">
                    {detailedResponses[category].map((item, qIndex) => (
                      <div key={qIndex} className="gradient-dark-blue p-3 rounded-md inner-shadow glass-effect">
                        <p className="text-sm font-medium text-gray-300 mb-1">
                          {qIndex + 1}. {item.question}
                        </p>
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-white">
                            <span className="text-gray-400">Answer:</span> {item.answer}
                          </p>
                          <span className="bg-dark-500 text-primary-500 px-2 py-1 rounded text-xs font-semibold">
                            {item.score}/2
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

    </motion.div>
  );
};

export default ResultsSection;